/* eslint-disable react/prop-types */
import React, { useState, useEffect, useRef } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  DatePicker,
  Card,
  Checkbox,
  Tag,
  Tooltip,
  Popconfirm,
  message,
} from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, SearchOutlined, DeleteOutlined, PaperClipOutlined,
} from '@ant-design/icons';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import Uploader from 'components/Uploader';
import { onError } from 'utils/handlers';
import { checkToken } from 'api/utils';
import { ESTATUS_ACTIVO, ESTATUS_AUTORIZADO } from 'utils/constants';
import moment from 'moment';
import { getAllAreas } from 'api/catalogos/DirectorioAreas';
import { getEvaluacionesAsistencia } from 'api/catalogos/TiposDeEvaluacionAsistencia';
import { getEmpleadosConDatosOpFilteredPaginated } from 'api/estructuraOrganizacional/empleados';
import {
  getJustificacionGrupalReloj,
  postJustificacionGrupalReloj,
  patchJustificacionGrupalReloj,
  getEmpleadosCandidatosGrupal,
  descartarDetalleJustificacionGrupal,
} from 'api/configuraciones/clock/Justificacion/JustificacionGrupal';
import {
  postDocumentosJustificacionGrupal,
  getDocumentosJustificacionesGrupal,
  deleteDocumentosJustificacionGrupal,
} from 'api/configuraciones/clock/Justificacion/JustificacionAdjuntosGrupal';
import EmpleadoMultiSearch from './EmpleadoMultiSearch';

const baseURIDatosOpPaginado = 'catalogos/dato-operacional-del-empleado-paginado/?empleados_filter=';

const ESTADOS_DETALLE_LABEL = {
  1: 'Activo',
  4: 'Autorizada',
  5: 'Cancelada',
  6: 'Rechazada',
};

const JustificacionGrupalForms = ({
  datos_padre, autorizar_rechazar, updateTable, refreshGeneral,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [areas, setAreas] = useState([]);
  const [tiposDeEvento, setTiposDeEvento] = useState([]);
  const [selectedData, setSelectedData] = useState({});
  const [detalles, setDetalles] = useState([]);
  const [disableFields, setDisableFields] = useState(false);
  const busquedaEmpleadosRequestId = useRef(0);

  const [candidatos, setCandidatos] = useState([]);
  const [selectedCandidatoKeys, setSelectedCandidatoKeys] = useState([]);
  const [manualEmpleados, setManualEmpleados] = useState([]);
  const [multiSearchOpen, setMultiSearchOpen] = useState(false);

  const [emisorEmpleado, setEmisorEmpleado] = useState(null);
  const [autorizoEmpleado, setAutorizoEmpleado] = useState(null);
  const [visibleCancelar, setVisibleCancelar] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);
  const [archivoOficio, setArchivoOficio] = useState(null);
  const [documentoOficio, setDocumentoOficio] = useState(null);
  const [loadingDocumento, setLoadingDocumento] = useState(false);

  const isEdit = !!datos_padre?._dataId;

  const resetSeleccion = () => {
    setCandidatos([]);
    setSelectedCandidatoKeys([]);
    setManualEmpleados([]);
    setArchivoOficio(null);
  };

  const handleUploadOficio = ({ file }) => {
    const realFile = file.originFileObj || file;
    if (!(realFile instanceof File)) {
      message.error('El archivo no es válido.');
      return;
    }
    setArchivoOficio(realFile);
  };

  const fetchDocumentoOficio = async (grupalId) => {
    if (!grupalId) {
      setDocumentoOficio(null);
      return;
    }
    setLoadingDocumento(true);
    const response = await getDocumentosJustificacionesGrupal({
      justificacion_reloj_checador_grupal: grupalId,
    });
    setDocumentoOficio(response?.results?.[0] || null);
    setLoadingDocumento(false);
  };

  const handleUploadOficioEdit = async ({ file }) => {
    const realFile = file.originFileObj || file;
    if (!(realFile instanceof File)) {
      message.error('El archivo no es válido.');
      return;
    }
    setLoadingDocumento(true);
    const formData = new FormData();
    formData.append('justificacion_reloj_checador_grupal', datos_padre._dataId);
    formData.append('archivo', realFile);
    formData.append('nombre_del_documento', realFile.name.replace(/\.[^/.]+$/, ''));
    formData.append('extension_del_documento', realFile.name.split('.').pop().toLowerCase());
    try {
      await postDocumentosJustificacionGrupal(formData);
      await fetchDocumentoOficio(datos_padre._dataId);
    } catch (err) {
      onError(err, setLoadingDocumento);
    }
  };

  const onEliminarDocumentoOficio = async () => {
    if (!documentoOficio?.id) return;
    setLoadingDocumento(true);
    const deleted = await deleteDocumentosJustificacionGrupal(documentoOficio.id);
    if (deleted) {
      await fetchDocumentoOficio(datos_padre._dataId);
    } else {
      setLoadingDocumento(false);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      resetSeleccion();
      setEmisorEmpleado(null);
      setAutorizoEmpleado(null);
      if (!datos_padre?._dataId) {
        form.resetFields();
        setSelectedData({});
        setDetalles([]);
        setDisableFields(false);
        setDocumentoOficio(null);
        setLoading(false);
        return;
      }
      const received = await getJustificacionGrupalReloj(datos_padre._dataId);
      if (received) {
        form.setFieldsValue({
          area_que_expide: received.area_que_expide?.id,
          tipo_de_evaluacion_de_asistencia: received.tipo_de_evaluacion_de_asistencia?.id,
          fecha_evento_desde: received.fecha_evento_desde
            ? moment(received.fecha_evento_desde) : null,
          fecha_evento_hasta: received.fecha_evento_hasta
            ? moment(received.fecha_evento_hasta) : null,
          numero_oficio: received.numero_oficio,
          fecha_oficio: received.fecha_oficio ? moment(received.fecha_oficio) : null,
          justificacion: received.justificacion,
          empleado_emisor: received.empleado_emisor?.numero_y_nombre_completo,
          empleado_autorizado_rechazado:
            received.empleado_autorizado_rechazado?.numero_y_nombre_completo,
          motivo_rechazo: received.motivo_rechazo,
        });
        setEmisorEmpleado(received.empleado_emisor || null);
        setAutorizoEmpleado(received.empleado_autorizado_rechazado || null);
        setSelectedData(received);
        setDetalles(received.detalles || []);
        setDisableFields(
          autorizar_rechazar || [ESTATUS_AUTORIZADO, 5].includes(received.estados_globales?.id),
        );
        await fetchDocumentoOficio(datos_padre._dataId);
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [datos_padre]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [_areas, _tiposDeEvento] = await Promise.all([
        getAllAreas({ estados_globales: ESTATUS_AUTORIZADO }),
        getEvaluacionesAsistencia({ estados_globales: ESTATUS_AUTORIZADO }),
      ]);
      if (mounted) {
        setAreas(_areas?.results || _areas || []);
        // "Puntual" no es un evento que amerite justificación grupal.
        setTiposDeEvento((_tiposDeEvento || []).filter((tipo) => tipo?.nombre?.toUpperCase() !== 'PUNTUAL'));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisibleCancelar(false);
    setConfirmationVisible(false);
  };

  const onBuscarEmpleados = async () => {
    try {
      const tipo = form.getFieldValue('tipo_de_evaluacion_de_asistencia');
      const desde = form.getFieldValue('fecha_evento_desde');
      const hasta = form.getFieldValue('fecha_evento_hasta');
      if (!tipo || !desde || !hasta) {
        message.warning('Selecciona el Tipo de Evento y el periodo antes de buscar empleados.');
        return;
      }
      const filtroArea = form.getFieldValue('filtro_area');
      const requestId = busquedaEmpleadosRequestId.current + 1;
      busquedaEmpleadosRequestId.current = requestId;
      setLoading(true);
      const response = await getEmpleadosCandidatosGrupal({
        tipo_de_evaluacion_de_asistencia: tipo,
        fecha_desde: desde.format('YYYY-MM-DD'),
        fecha_hasta: hasta.format('YYYY-MM-DD'),
        ...(filtroArea && { directorio_de_area: filtroArea }),
      });
      if (requestId !== busquedaEmpleadosRequestId.current) {
        // Llegó una respuesta de una búsqueda anterior después de una más
        // reciente: se descarta para no pisar los resultados vigentes.
        return;
      }
      const rows = (response || []).map((row) => ({
        id: row.empleado_id,
        numero_y_nombre_completo: row.empleado__numero_y_nombre_completo,
        plaza: row.empleado__dato_operacional_del_empleado__plaza__clave_y_descripcion,
        puesto: row.empleado__dato_operacional_del_empleado__plaza__puesto__clave_y_descripcion,
        area: row.empleado__dato_operacional_del_empleado__plaza__directorio_de_area__nombre,
        dias_afectados: row.dias_afectados,
      }));
      setCandidatos(rows);
      setSelectedCandidatoKeys([]);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const idsYaSeleccionados = [
    ...selectedCandidatoKeys,
    ...manualEmpleados.map((emp) => emp.id),
  ];

  const onFinishAgregarManual = (empleados) => {
    setManualEmpleados((prev) => [
      ...prev,
      ...empleados
        .filter((emp) => !idsYaSeleccionados.includes(emp.id))
        .map((emp) => ({
          id: emp.id,
          numero_y_nombre_completo: emp.numero_y_nombre_completo
            || [emp.nombre, emp.apellido_paterno, emp.apellido_materno].filter(Boolean).join(' '),
        })),
    ]);
  };

  const onQuitarManual = (id) => {
    setManualEmpleados((prev) => prev.filter((emp) => emp.id !== id));
  };

  const handleOnRowClickCandidato = (record) => {
    setSelectedCandidatoKeys((prev) => (
      prev.includes(record.id) ? prev.filter((id) => id !== record.id) : [...prev, record.id]
    ));
  };

  const candidatosRowSelection = {
    selectedRowKeys: selectedCandidatoKeys,
    type: 'checkbox',
    columnTitle: (
      <Checkbox
        checked={candidatos.length > 0 && selectedCandidatoKeys.length === candidatos.length}
        indeterminate={
          selectedCandidatoKeys.length > 0 && selectedCandidatoKeys.length < candidatos.length
        }
        onChange={(e) => {
          setSelectedCandidatoKeys(e.target.checked ? candidatos.map((row) => row.id) : []);
        }}
      />
    ),
    onSelect: (record, selected) => {
      setSelectedCandidatoKeys((prev) => (
        selected ? [...prev, record.id] : prev.filter((id) => id !== record.id)
      ));
    },
    onChange: (keys) => setSelectedCandidatoKeys(keys),
  };

  const candidatosColumns = [
    {
      titleText: 'Empleado', dataIndex: 'numero_y_nombre_completo', key: 'numero_y_nombre_completo', width: 260,
    },
    {
      titleText: 'Puesto', dataIndex: 'puesto', key: 'puesto', width: 170,
    },
    {
      titleText: 'Plaza', dataIndex: 'plaza', key: 'plaza', width: 170,
    },
    {
      titleText: 'Área', dataIndex: 'area', key: 'area', width: 170,
    },
    {
      titleText: 'Días Afectados', dataIndex: 'dias_afectados', key: 'dias_afectados', width: 120,
    },
  ];

  const onDescartarDetalle = async (detalleId) => {
    setLoading(true);
    const result = await descartarDetalleJustificacionGrupal(datos_padre._dataId, detalleId);
    if (result) {
      await fetchData();
      refreshGeneral?.();
    } else {
      setLoading(false);
    }
  };

  const puedeDescartarDetalle = selectedData?.estados_globales?.id === ESTATUS_ACTIVO;

  // El oficio se puede adjuntar mientras la justificación grupal siga Activa
  // (incluso en la pantalla de Autorizar/Rechazar); una vez Autorizada o
  // Cancelada ya no debe modificarse. No se usa `disableFields` aquí porque
  // ese flag también se activa solo por estar en modo autorizar_rechazar,
  // lo que bloquearía adjuntar el oficio precisamente cuando más se necesita.
  const documentosDisabled = [ESTATUS_AUTORIZADO, 5].includes(selectedData?.estados_globales?.id);

  const detallesColumns = [
    {
      titleText: 'Empleado', dataIndex: 'empleado_nombre', key: 'empleado_nombre', width: 260,
    },
    {
      titleText: 'Puesto', dataIndex: 'puesto', key: 'puesto', width: 220,
    },
    {
      titleText: 'Plaza', dataIndex: 'plaza', key: 'plaza', width: 220,
    },
    {
      titleText: 'Fecha', dataIndex: 'fecha', key: 'fecha', width: 130,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 130,
      render: (value) => ESTADOS_DETALLE_LABEL[value] || value,
    },
    {
      titleText: 'Acciones',
      dataIndex: 'id',
      key: 'acciones',
      width: 120,
      render: (id) => (
        <Popconfirm
          title="¿Descartar este registro de la justificación grupal?"
          onConfirm={() => onDescartarDetalle(id)}
          okText="Sí"
          cancelText="No"
          disabled={!puedeDescartarDetalle}
        >
          <Button danger size="small" icon={<DeleteOutlined />} disabled={!puedeDescartarDetalle}>
            Descartar
          </Button>
        </Popconfirm>
      ),
    },
  ];

  /** Busca un empleado por su número y, si existe, actualiza el campo de
   * despliegue y el estado que guarda su id real (PK) para enviar al backend. */
  const buscarEmpleadoPorNumero = async (field, setEmpleadoState) => {
    const searchEmp = form.getFieldValue(field);
    if (!searchEmp) {
      message.warning('Escribe el número de empleado y presiona buscar.');
      return;
    }
    setLoading(true);
    const res = await getEmpleadosConDatosOpFilteredPaginated(
      `${baseURIDatosOpPaginado}numero_de_empleado=${searchEmp};`,
    );
    const registro = res?.results?.[0];
    const empleadoResult = registro?.empleado;
    if (empleadoResult) {
      setEmpleadoState({
        ...empleadoResult,
        puesto_clave_y_descripcion: registro?.puesto_clave_y_descripcion,
        plaza_clave_y_descripcion: registro?.plaza_clave_y_descripcion,
      });
      form.setFieldsValue({ [field]: empleadoResult.numero_y_nombre_completo });
    } else {
      message.warning('No se encontró un empleado con ese número.');
      form.resetFields([field]);
    }
    setLoading(false);
  };

  const buildPayload = () => {
    const values = form.getFieldsValue();
    return {
      area_que_expide: values.area_que_expide,
      tipo_de_evaluacion_de_asistencia: values.tipo_de_evaluacion_de_asistencia,
      fecha_evento_desde: values.fecha_evento_desde.format('YYYY-MM-DD'),
      fecha_evento_hasta: values.fecha_evento_hasta.format('YYYY-MM-DD'),
      numero_oficio: values.numero_oficio,
      fecha_oficio: values.fecha_oficio.format('YYYY-MM-DD'),
      justificacion: values.justificacion,
      empleado_emisor: emisorEmpleado?.id,
    };
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields([
        'area_que_expide', 'tipo_de_evaluacion_de_asistencia', 'fecha_evento_desde',
        'fecha_evento_hasta', 'numero_oficio', 'fecha_oficio', 'justificacion', 'empleado_emisor',
      ]);
      if (!emisorEmpleado?.id) {
        message.warning('Busca y confirma al Emisor del Oficio.');
        setLoading(false);
        return;
      }
      if (!idsYaSeleccionados.length) {
        message.warning('Selecciona al menos un empleado (del filtro o agregado manualmente).');
        setLoading(false);
        return;
      }
      const payload = {
        ...buildPayload(),
        empleados_ids: idsYaSeleccionados,
      };
      const saved = await postJustificacionGrupalReloj(payload);
      if (saved) {
        if (archivoOficio) {
          const formData = new FormData();
          formData.append('justificacion_reloj_checador_grupal', saved.id);
          formData.append('archivo', archivoOficio);
          formData.append('nombre_del_documento', archivoOficio.name.replace(/\.[^/.]+$/, ''));
          formData.append('extension_del_documento', archivoOficio.name.split('.').pop().toLowerCase());
          await postDocumentosJustificacionGrupal(formData);
        }
        resetSeleccion();
        form.resetFields();
        setEmisorEmpleado(null);
        await updateTable();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      await patchJustificacionGrupalReloj(datos_padre._dataId, {
        estados_globales: ESTATUS_AUTORIZADO,
        empleado_autorizado_rechazado: autorizoEmpleado?.id,
      });
      onCancel();
      await updateTable();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onConfirmRechazar = async () => {
    try {
      setLoading(true);
      await patchJustificacionGrupalReloj(datos_padre._dataId, {
        motivo_rechazo: form.getFieldValue('motivo_rechazo'),
        empleado_autorizado_rechazado: autorizoEmpleado?.id,
      });
      onCancel();
      await updateTable();
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const requiredRule = { required: true, message: 'El campo es requerido' };
  const rules = { required: [requiredRule] };

  const nombreDocumentoOficio = (() => {
    const nombre = documentoOficio?.nombre_del_documento || '';
    const extension = documentoOficio?.extension_del_documento?.toLowerCase() || '';
    if (extension && nombre.toLowerCase().endsWith(`.${extension}`)) return nombre;
    return extension ? `${nombre}.${extension}` : nombre;
  })();

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container"
          extra={(
            <FormSubmitControls
              onFinish={!autorizar_rechazar && !isEdit ? onFinish : null}
              onFinishText="Guardar Justificación Grupal"
              customActions={
                autorizar_rechazar ? [
                  {
                    onClick: async () => {
                      try {
                        if (form.getFieldValue('motivo_rechazo')) {
                          throw new Error('El motivo de rechazo debe estar vacío al autorizar');
                        }
                        if (!autorizoEmpleado?.id) {
                          throw new Error('Busca y confirma quién autoriza');
                        }
                        setConfirmationVisible(true);
                      } catch (err) {
                        message.warning(err.message);
                      }
                    },
                    icon: <CheckCircleOutlined />,
                    permission: { model: 'justificacionrelojchecadorgrupal', action: 'autorizar' },
                    text: 'Autorizar',
                    disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
                    className: 'success',
                  },
                  {
                    onClick: async () => {
                      try {
                        if (!form.getFieldValue('motivo_rechazo')) {
                          throw new Error('El motivo de rechazo es obligatorio');
                        }
                        if (!autorizoEmpleado?.id) {
                          throw new Error('Busca y confirma quién rechaza');
                        }
                        setVisibleCancelar(true);
                      } catch (err) {
                        message.warning(err.message);
                      }
                    },
                    text: 'Rechazar',
                    icon: <CloseCircleOutlined />,
                    className: 'cancel',
                    disabled: selectedData?.estados_globales?.id !== ESTATUS_ACTIVO,
                  },
                ] : []
              }
            />
          )}
          bordered={false}
        >
          <Form layout="vertical" form={form} scrollToFirstError name="justificacion_grupal">
            <Row gutter={10}>
              <Col xs={24} sm={8}>
                <Form.Item name="tipo_de_evaluacion_de_asistencia" label="Tipo de Evento" rules={rules.required}>
                  <Select
                    labelProp="nombre"
                    keyProp="id"
                    dataSource={tiposDeEvento}
                    disabled={disableFields || isEdit}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="fecha_evento_desde" label="Fecha del Evento (Desde)" rules={rules.required}>
                  <DatePicker
                    allowClear
                    format="YYYY-MM-DD"
                    disabled={disableFields || isEdit}
                    disabledDate={(current) => {
                      const hasta = form.getFieldValue('fecha_evento_hasta');
                      return !!(hasta && current && current.isAfter(hasta, 'day'));
                    }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="fecha_evento_hasta" label="Fecha del Evento (Hasta)" rules={rules.required}>
                  <DatePicker
                    allowClear
                    format="YYYY-MM-DD"
                    disabled={disableFields || isEdit}
                    disabledDate={(current) => {
                      const desde = form.getFieldValue('fecha_evento_desde');
                      return !!(desde && current && current.isBefore(desde, 'day'));
                    }}
                  />
                </Form.Item>
              </Col>
              {!isEdit && (
                <Col xs={24} sm={8}>
                  <Form.Item name="filtro_area" label="Filtrar por Área">
                    <Select
                      labelProp="nombre"
                      keyProp="id"
                      dataSource={areas}
                      allowClear
                      showSearch
                    />
                  </Form.Item>
                </Col>
              )}
              <Col xs={24} sm={8}>
                <Form.Item name="area_que_expide" label="Área que Expide el Oficio" rules={rules.required}>
                  <Select
                    labelProp="nombre"
                    keyProp="id"
                    dataSource={areas}
                    disabled={disableFields}
                    showSearch
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="numero_oficio" label="Número de Oficio" rules={rules.required}>
                  <Input allowClear disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={8}>
                <Form.Item name="fecha_oficio" label="Fecha de Oficio" rules={rules.required}>
                  <DatePicker allowClear format="YYYY-MM-DD" disabled={disableFields} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Form.Item
                    name="empleado_emisor"
                    label="Emisor del Oficio (No. de Empleado)"
                    rules={rules.required}
                    hasFeedback
                    style={{ flex: 1, minWidth: 0 }}
                  >
                    <Input allowClear disabled={disableFields} />
                  </Form.Item>
                  <Button
                    loading={loading}
                    onClick={() => buscarEmpleadoPorNumero('empleado_emisor', setEmisorEmpleado)}
                    icon={<SearchOutlined />}
                    style={{ width: '2.7rem', height: '2rem' }}
                    disabled={disableFields}
                  />
                </div>
              </Col>
              {!!emisorEmpleado?.id && (
                <>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="Puesto del Emisor">
                      <Input value={emisorEmpleado?.puesto_clave_y_descripcion} disabled />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item label="Plaza del Emisor">
                      <Input value={emisorEmpleado?.plaza_clave_y_descripcion} disabled />
                    </Form.Item>
                  </Col>
                </>
              )}
              {!isEdit && (
                <Col xs={24} sm={8}>
                  <Form.Item name="archivo_oficio" valuePropName="file" style={{ marginBottom: 0 }}>
                    <Uploader
                      title="Documento del Oficio (opcional)"
                      limitMB={5}
                      allowedExtensions={['jpg', 'png', 'jpeg', 'pdf']}
                      allowPreview={false}
                      fullSize
                      onChange={handleUploadOficio}
                    />
                  </Form.Item>
                </Col>
              )}
              {autorizar_rechazar && (
                <>
                  <Col xs={24} sm={24} md={8}>
                    <Row align="middle">
                      <Col xs={24} sm={24} md={24}>
                        <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <Form.Item
                            name="empleado_autorizado_rechazado"
                            label="Autorizó / Rechazó (No. de Empleado)"
                            hasFeedback
                            style={{ flex: 1, minWidth: 0 }}
                          >
                            <Input allowClear />
                          </Form.Item>
                          <Button
                            loading={loading}
                            onClick={() => buscarEmpleadoPorNumero('empleado_autorizado_rechazado', setAutorizoEmpleado)}
                            icon={<SearchOutlined />}
                            style={{ width: '2.7rem', height: '2rem' }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Col>
                  <Col xs={24} sm={24} md={8}>
                    <Spin spinning={loadingDocumento}>
                      {documentoOficio ? (
                        <>
                          <div style={{ marginBottom: 8 }}>Documento del Oficio</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <PaperClipOutlined />
                            <a href={documentoOficio.archivo} target="_blank" rel="noreferrer">
                              {nombreDocumentoOficio}
                            </a>
                            {!documentosDisabled && (
                              <Popconfirm
                                title="¿Eliminar el documento del oficio?"
                                onConfirm={onEliminarDocumentoOficio}
                                okText="Sí"
                                cancelText="No"
                              >
                                <Button danger size="small" icon={<DeleteOutlined />} />
                              </Popconfirm>
                            )}
                          </div>
                        </>
                      ) : (
                        <Uploader
                          title="Documento del Oficio"
                          limitMB={5}
                          allowedExtensions={['jpg', 'png', 'jpeg', 'pdf']}
                          allowPreview={false}
                          fullSize
                          disabled={documentosDisabled}
                          onChange={handleUploadOficioEdit}
                        />
                      )}
                    </Spin>
                  </Col>
                  {autorizar_rechazar && (
                  <Col xs={24} sm={24} md={8}>
                    <Tooltip title="Se ignora este campo al autorizar, obligatorio al rechazar">
                      <Form.Item name="motivo_rechazo" label="Motivo de Rechazo">
                        <Input allowClear />
                      </Form.Item>
                    </Tooltip>
                  </Col>
                  )}
                </>
              )}
              <Col xs={24} sm={16}>
                <Form.Item name="justificacion" label="Justificación / Motivo del Oficio" rules={rules.required}>
                  <Input.TextArea allowClear disabled={disableFields} />
                </Form.Item>
              </Col>

            </Row>
          </Form>

          {!isEdit && (
            <>
              <Row style={{ marginBottom: 10 }}>
                <Col xs={24}>
                  <Button onClick={onBuscarEmpleados}>Buscar Empleados</Button>
                  {' '}
                  <Button onClick={() => setMultiSearchOpen(true)}>
                    Agregar Empleado Manualmente
                  </Button>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <Table
                    allowColumnSelector={false}
                    cols={candidatosColumns}
                    data={candidatos}
                    rowSelection={candidatosRowSelection}
                    handleOnRowClick={handleOnRowClickCandidato}
                    allowExportParam={false}
                    allowSearch={false}
                  />
                </Col>
              </Row>
              {!!manualEmpleados.length && (
                <Row style={{ marginTop: 10 }}>
                  <Col xs={24}>
                    {manualEmpleados.map((emp) => (
                      <Tag
                        key={emp.id}
                        closable
                        onClose={() => onQuitarManual(emp.id)}
                        style={{ marginBottom: 4 }}
                      >
                        {emp.numero_y_nombre_completo}
                      </Tag>
                    ))}
                  </Col>
                </Row>
              )}
            </>
          )}

          {isEdit && (
            <Row style={{ marginTop: 10 }}>
              <Col xs={24}>
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 10 }}>
                  Empleados Incluidos
                </div>
                <Table
                  allowColumnSelector={false}
                  cols={detallesColumns}
                  data={detalles}
                  allowExportParam={false}
                  allowSearch={false}
                />
              </Col>
            </Row>
          )}
        </Card>

        <EmpleadoMultiSearch
          open={multiSearchOpen}
          setVisible={setMultiSearchOpen}
          onFinish={onFinishAgregarManual}
          excludedIds={idsYaSeleccionados}
        />
        <ModalConfirmation
          onFinish={onConfirmRechazar}
          Confirm
          onCancel={() => setVisibleCancelar(false)}
          open={visibleCancelar}
          contentTitle={`Rechazar la justificación grupal ${selectedData?.numero_oficio || ''}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={onConfirmAutorizar}
          ConfirmAutorize
          onCancel={() => setConfirmationVisible(false)}
          open={confirmationVisible}
          contentTitle={`Autorizar la justificación grupal ${selectedData?.numero_oficio || ''}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default JustificacionGrupalForms;
